import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useState, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { NumberRow } from "@/components/NumberPill";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useAllGames, useResults } from "@/hooks/use-query-hooks";
import { GamePicker } from "@/components/GamePicker";
import { savePick, type SavedPick } from "@/lib/local-storage";
import { Sparkles, Save, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { useServerFn } from "@tanstack/react-start";
import { createSavedPick } from "@/lib/saved-picks.functions";
import { useQueryClient } from "@tanstack/react-query";

type Mode = "random" | "hot" | "cold" | "balanced";

type Search = { game?: string };

export const Route = createFileRoute("/tools/number-generator")({
  validateSearch: (s: Record<string, unknown>): Search => ({ game: typeof s.game === "string" ? s.game : undefined }),
  head: () => buildSeo({
    title: "Number Generator — Lottery Tools | USA Lottery Club",
    description: "Generate random or strategy-based lottery numbers. Choose from Random, Hot Bias, Cold Bias, and Balanced modes.",
    path: "/tools/number-generator",
  }),
  component: NumberGenerator,
});

function NumberGenerator() {
  const { game: initial } = Route.useSearch();
  const { data: games, isLoading } = useAllGames();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const createPickFn = useServerFn(createSavedPick);
  const [selectedGame, setSelectedGame] = useState(initial || "");
  const [mode, setMode] = useState<Mode>("random");
  const [count, setCount] = useState(5);
  const [generated, setGenerated] = useState<number[][]>([]);

  const game = games?.find((g) => g.slug === selectedGame);
  const { data: results } = useResults(game?.id, 50);

  const freqMap = useMemo(() => {
    if (!results) return {} as Record<number, number>;
    const freq: Record<number, number> = {};
    results.forEach((r) => {
      (r.winning_numbers || []).forEach((n) => { freq[n] = (freq[n] || 0) + 1; });
    });
    return freq;
  }, [results]);

  function generate() {
    if (!game) return;
    const sets: number[][] = [];
    for (let s = 0; s < count; s++) {
      const mainRange = game.number_range_main;
      const mainCount = game.number_count_main;
      const pool = Array.from({ length: mainRange }, (_, i) => i + 1);
      let weighted = pool;
      if (mode === "hot" && Object.keys(freqMap).length > 0) {
        weighted = [...pool].sort((a, b) => (freqMap[b] || 0) - (freqMap[a] || 0)).slice(0, Math.max(mainCount * 3, 15));
      } else if (mode === "cold" && Object.keys(freqMap).length > 0) {
        weighted = [...pool].sort((a, b) => (freqMap[a] || 0) - (freqMap[b] || 0)).slice(0, Math.max(mainCount * 3, 15));
      }
      const selected: number[] = [];
      const available = [...weighted];
      for (let i = 0; i < mainCount; i++) {
        const idx = Math.floor(Math.random() * available.length);
        selected.push(available.splice(idx, 1)[0]);
      }
      selected.sort((a, b) => a - b);
      const bonusCount = game.number_count_bonus || 0;
      const bonusRange = game.number_range_bonus || 0;
      const bonus: number[] = [];
      if (bonusCount > 0 && bonusRange > 0) {
        const bonusPool = Array.from({ length: bonusRange }, (_, i) => i + 1);
        for (let i = 0; i < bonusCount; i++) {
          const idx = Math.floor(Math.random() * bonusPool.length);
          bonus.push(bonusPool.splice(idx, 1)[0]);
        }
      }
      sets.push([...selected, ...bonus]);
    }
    setGenerated(sets);
  }

  function handleSave(nums: number[]) {
    if (!game) return;
    const numbers = nums.slice(0, game.number_count_main);
    const bonus_numbers = nums.slice(game.number_count_main);
    if (user) {
      createPickFn({
        data: {
          game_slug: game.slug,
          game_name: game.name,
          numbers,
          bonus_numbers,
        },
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["saved_picks"] });
          toast.success("Saved & synced to your account!");
        })
        .catch((e: Error) => toast.error(e.message));
    } else {
      const pick: SavedPick = {
        id: crypto.randomUUID(),
        gameSlug: game.slug,
        gameName: game.name,
        numbers,
        bonusNumbers: bonus_numbers,
        createdAt: new Date().toISOString(),
      };
      savePick(pick);
      toast.success("Saved to My Picks (sign in to sync)!");
    }
  }

  function handleCopy(nums: number[]) {
    if (!game) return;
    const main = nums.slice(0, game.number_count_main).join(", ");
    const bonus = nums.slice(game.number_count_main);
    const text = bonus.length > 0 ? `${main} + ${bonus.join(", ")}` : main;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  }

  return (
    <Layout>
      <SEOHead title="Number Generator — Lottery Tools" description="Generate random or strategy-based lottery numbers." />
      <div className="container py-6 md:py-10 max-w-2xl">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/tools">Tools</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Number Generator</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="font-display text-2xl md:text-3xl font-bold mb-6">
          <Sparkles className="inline h-6 w-6 mr-2 text-primary" /> Lottery Number Generator Tool
        </h1>
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
          <Card className="mb-6">
            <CardContent className="pt-6 space-y-4">
              <GamePicker games={games} selectedSlug={selectedGame} onSelect={setSelectedGame} />
              <div>
                <Label>Mode</Label>
                <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                  <SelectTrigger aria-label="Generation mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="random">Random</SelectItem>
                    <SelectItem value="hot">Hot Bias</SelectItem>
                    <SelectItem value="cold">Cold Bias</SelectItem>
                    <SelectItem value="balanced">Balanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Number of Sets ({count})</Label>
                <Select value={String(count)} onValueChange={(v) => setCount(parseInt(v))}>
                  <SelectTrigger aria-label="Number of sets"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 3, 5, 10, 20].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={!selectedGame} className="w-full">
                <Sparkles className="h-4 w-4 mr-2" /> Generate Numbers
              </Button>
            </CardContent>
          </Card>
        )}
        {generated.length > 0 && game && (
          <div className="space-y-3">
            {generated.map((nums, i) => (
              <Card key={i}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <span className="text-sm text-muted-foreground font-mono w-6">#{i + 1}</span>
                  <div className="flex-1">
                    <NumberRow numbers={nums.slice(0, game.number_count_main)} bonusNumbers={nums.slice(game.number_count_main)} size="sm" />
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" aria-label="Save numbers" onClick={() => handleSave(nums)} title="Save"><Save className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" aria-label="Copy numbers" onClick={() => handleCopy(nums)} title="Copy"><Copy className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}